import { useEffect, useRef } from "react";
var isRecord = function (val) {
    return val !== null && typeof val === "object";
};
var hasMessage = function (val) {
    return isRecord(val) && "message" in val;
};
var getFirstErrorPath = function (obj, path) {
    if (path === void 0) { path = ""; }
    for (var _i = 0, _a = Object.keys(obj); _i < _a.length; _i++) {
        var key = _a[_i];
        var value = obj[key];
        var currentPath = path ? "".concat(path, ".").concat(key) : key;
        if (hasMessage(value)) {
            return currentPath;
        }
        if (isRecord(value)) {
            var nestedPath = getFirstErrorPath(value, currentPath);
            if (nestedPath)
                return nestedPath;
        }
    }
    return null;
};
/**
 * Collects EVERY invalid field (path + message) from a react-hook-form error
 * tree. Used to build a human-readable summary when a field cannot be focused
 * in the DOM, so the user always sees which fields need attention.
 */
var collectInvalidFields = function (obj, path, acc) {
    if (path === void 0) { path = ""; }
    if (acc === void 0) { acc = []; }
    for (var _i = 0, _a = Object.keys(obj); _i < _a.length; _i++) {
        var key = _a[_i];
        var value = obj[key];
        var currentPath = path ? "".concat(path, ".").concat(key) : key;
        if (hasMessage(value)) {
            acc.push({
                path: currentPath,
                message: typeof value.message === "string" ? value.message : currentPath,
            });
        }
        else if (isRecord(value)) {
            collectInvalidFields(value, currentPath, acc);
        }
    }
    return acc;
};
var MAX_FOCUS_ATTEMPTS = 3;
/**
 * Scrolls to and focuses the first invalid form field. When the field cannot be
 * located in the DOM (e.g. it lives inside a collapsed accordion, or the error
 * sits on a value object that has no matching input), it first asks the caller
 * to expand the relevant section and retries across a few animation frames; if
 * the field still cannot be reached it hands the complete list of invalid
 * fields to `onUnresolvedErrors` so the submit never fails silently.
 *
 * @param errors react-hook-form error tree.
 * @param handlers optional recovery callbacks (accordion expand + fallback).
 */
export var scrollToError = function (errors, handlers) {
    var _a, _b;
    var firstErrorField = getFirstErrorPath(errors);
    if (!firstErrorField)
        return;
    // Ask the caller to reveal the section holding the field so it can be focused.
    (_a = handlers === null || handlers === void 0 ? void 0 : handlers.openSectionForField) === null || _a === void 0 ? void 0 : _a.call(handlers, firstErrorField);
    var focusField = function () {
        var errorElement = document.querySelector("[name=\"".concat(firstErrorField, "\"]"));
        if (errorElement instanceof HTMLElement) {
            errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
            errorElement.focus();
            return true;
        }
        return false;
    };
    if (focusField())
        return;
    // The field is not in the DOM yet (freshly expanded section) or has no
    // matching input at all. Retry across a few frames before falling back.
    if (typeof requestAnimationFrame !== "function") {
        (_b = handlers === null || handlers === void 0 ? void 0 : handlers.onUnresolvedErrors) === null || _b === void 0 ? void 0 : _b.call(handlers, collectInvalidFields(errors));
        return;
    }
    var attempts = 0;
    var retry = function () {
        var _a;
        attempts += 1;
        if (focusField())
            return;
        if (attempts < MAX_FOCUS_ATTEMPTS) {
            requestAnimationFrame(retry);
            return;
        }
        (_a = handlers === null || handlers === void 0 ? void 0 : handlers.onUnresolvedErrors) === null || _a === void 0 ? void 0 : _a.call(handlers, collectInvalidFields(errors));
    };
    requestAnimationFrame(retry);
};
export var useScrollOnPotentialDuplicates = function (potentialDuplicates) {
    var topRef = useRef(null);
    useEffect(function () {
        if (potentialDuplicates.length > 0 && (topRef === null || topRef === void 0 ? void 0 : topRef.current)) {
            requestAnimationFrame(function () {
                var _a;
                (_a = topRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: "smooth" });
            });
        }
    }, [potentialDuplicates, topRef]);
    return { topRef: topRef };
};
